import React from 'react';
import { Group, Circle, Rect, Line, Text } from 'react-konva';

/**
 * TechnicalIcons Component
 * Renderiza símbolos gráficos prediais para marcenaria (NBR 5444):
 * - Tomada Baixa (h = 30cm)
 * - Tomada Média (h = 110cm)
 * - Tomada Alta (h = 220cm)
 * - Tomada 220V Especial
 * - Ponto de Água Fria / Quente
 * - Esgoto / Ralo
 * - Ponto de Gás
 * - Interruptor
 */
export const TECHNICAL_POINTS_CATALOG = [
  { id: 'outlet_low', label: 'Tomada Baixa (30cm)', category: 'electrical', defaultHeight: 30, icon: '▽', color: '#f59e0b' },
  { id: 'outlet_mid', label: 'Tomada Bancada (110cm)', category: 'electrical', defaultHeight: 110, icon: '◩', color: '#f59e0b' },
  { id: 'outlet_high', label: 'Tomada Alta (220cm)', category: 'electrical', defaultHeight: 220, icon: '▼', color: '#f59e0b' },
  { id: 'outlet_220v', label: 'Tomada 220V Especial', category: 'electrical', defaultHeight: 110, icon: '⚡', color: '#ef4444' },
  { id: 'switch', label: 'Interruptor', category: 'electrical', defaultHeight: 110, icon: 'S', color: '#6366f1' },
  { id: 'water_cold', label: 'Água Fria (AF)', category: 'hydraulic', defaultHeight: 60, icon: '💧', color: '#0284c7' },
  { id: 'water_hot', label: 'Água Quente (AQ)', category: 'hydraulic', defaultHeight: 60, icon: '🔥', color: '#ea580c' },
  { id: 'sewage', label: 'Esgoto / Ralo', category: 'hydraulic', defaultHeight: 50, icon: '⭕', color: '#64748b' },
  { id: 'gas', label: 'Ponto de Gás', category: 'gas', defaultHeight: 75, icon: '🔥', color: '#eab308' }
];

export const TechnicalPoint = ({
  element,
  isSelected,
  onClick,
  onTap
}) => {
  const { x = 0, y = 0, pointType = 'outlet_mid', heightFromFloorCm = 110, notes = '' } = element;
  const def = TECHNICAL_POINTS_CATALOG.find(p => p.id === pointType) || TECHNICAL_POINTS_CATALOG[1];

  const strokeColor = isSelected ? '#ef4444' : def.color;
  const radius = 14;

  const renderSymbol = () => {
    switch (pointType) {
      case 'outlet_low':
        // Triângulo vazado
        return (
          <Line
            points={[0, -radius + 4, radius - 4, radius - 4, -radius + 4, radius - 4]}
            closed
            stroke={strokeColor}
            strokeWidth={2}
            fill="white"
          />
        );
      case 'outlet_mid':
        // Triângulo meio preenchido
        return (
          <Group>
            <Line
              points={[0, -radius + 4, radius - 4, radius - 4, -radius + 4, radius - 4]}
              closed
              stroke={strokeColor}
              strokeWidth={2}
              fill="white"
            />
            <Line
              points={[0, -radius + 4, 0, radius - 4, -radius + 4, radius - 4]}
              closed
              fill={strokeColor}
            />
          </Group>
        );
      case 'outlet_high':
        // Triângulo totalmente preenchido
        return (
          <Line
            points={[0, -radius + 4, radius - 4, radius - 4, -radius + 4, radius - 4]}
            closed
            fill={strokeColor}
            stroke={strokeColor}
            strokeWidth={2}
          />
        );
      case 'outlet_220v':
        return (
          <Group>
            <Line
              points={[0, -radius + 4, radius - 4, radius - 4, -radius + 4, radius - 4]}
              closed
              fill="#fef2f2"
              stroke="#ef4444"
              strokeWidth={2}
            />
            <Text
              text="220"
              x={-10}
              y={-3}
              fontSize={8}
              fontStyle="bold"
              fill="#ef4444"
              align="center"
            />
          </Group>
        );
      case 'switch':
        return (
          <Group>
            <Circle radius={radius - 3} fill="white" stroke={strokeColor} strokeWidth={2} />
            <Text text="S" x={-4} y={-5} fontSize={10} fontStyle="bold" fill={strokeColor} />
          </Group>
        );
      case 'water_cold':
        return (
          <Group>
            <Circle radius={radius - 3} fill="#e0f2fe" stroke="#0284c7" strokeWidth={2} />
            <Text text="AF" x={-6} y={-4} fontSize={9} fontStyle="bold" fill="#0284c7" />
          </Group>
        );
      case 'water_hot':
        return (
          <Group>
            <Circle radius={radius - 3} fill="#ffedd5" stroke="#ea580c" strokeWidth={2} />
            <Text text="AQ" x={-7} y={-4} fontSize={9} fontStyle="bold" fill="#ea580c" />
          </Group>
        );
      case 'sewage':
        return (
          <Group>
            <Circle radius={radius - 3} fill="#f1f5f9" stroke="#64748b" strokeWidth={2} dash={[3, 2]} />
            <Text text="ESG" x={-9} y={-4} fontSize={8} fontStyle="bold" fill="#475569" />
          </Group>
        );
      case 'gas':
        return (
          <Group>
            <Rect x={-radius + 3} y={-radius + 3} width={radius * 2 - 6} height={radius * 2 - 6} fill="#fefce8" stroke="#ca8a04" strokeWidth={2} cornerRadius={3} />
            <Text text="GÁS" x={-9} y={-4} fontSize={8} fontStyle="bold" fill="#ca8a04" />
          </Group>
        );
      default:
        return <Circle radius={radius - 3} fill="white" stroke={strokeColor} strokeWidth={2} />;
    }
  };

  const labelText = `h=${heightFromFloorCm}cm`;

  return (
    <Group x={x} y={y} onClick={onClick} onTap={onTap}>
      {/* Halo de Seleção */}
      {isSelected && (
        <Circle
          radius={radius + 4}
          stroke="#ef4444"
          strokeWidth={1.5}
          dash={[4, 2]}
        />
      )}

      {/* Símbolo do Ponto */}
      {renderSymbol()}

      {/* Etiqueta de Altura do Piso */}
      <Rect
        x={-20}
        y={radius + 2}
        width={40}
        height={13}
        fill="white"
        stroke={isSelected ? '#ef4444' : '#cbd5e1'}
        strokeWidth={0.8}
        cornerRadius={2}
      />
      <Text
        text={labelText}
        x={-20}
        y={radius + 3}
        width={40}
        align="center"
        fontSize={8}
        fontStyle="bold"
        fill="#334155"
      />

      {/* Nota opcional se houver */}
      {notes && isSelected && (
        <Group y={radius + 18}>
          <Rect
            x={-notes.length * 3 - 6}
            y={0}
            width={notes.length * 6 + 12}
            height={14}
            fill="#1e293b"
            cornerRadius={3}
          />
          <Text
            text={notes}
            x={-notes.length * 3 - 6}
            y={2}
            width={notes.length * 6 + 12}
            align="center"
            fontSize={8}
            fill="white"
          />
        </Group>
      )}
    </Group>
  );
};

export default TechnicalPoint;
